import { apiClient } from './client'
import type { CursorPage } from './accounts'
import { ApiError } from './problem'
import type { AuthorityInteractionView, SubmissionPackageView } from '../types/authority'

/**
 * `GET /api/v1/program-filings/{programFilingId}/authority-interactions` (docs/api-spec.md §15,
 * IB-14, already published — `AuthorityInteractionController.list`). No additive backend route was
 * needed for this Case-workspace tab (GA demo lifecycle-tabs pass): this read already existed on
 * the wire before this story. One page (backend default limit) is fetched — a filing with more
 * interactions than that has its overflow disclosed by the panel via `hasMore`, never silently
 * dropped or paged behind a second UI this summary tab doesn't need.
 */
export async function listAuthorityInteractions(
  programFilingId: string,
  signal?: AbortSignal,
): Promise<CursorPage<AuthorityInteractionView>> {
  const { data } = await apiClient.get<CursorPage<AuthorityInteractionView>>(
    `/program-filings/${programFilingId}/authority-interactions`,
    { signal },
  )
  return data
}

/**
 * `GET /api/v1/submission-packages/{id}` (docs/api-spec.md §15, IB-14, already published —
 * `SubmissionPackageController.get`). Returns `null` (never throws) on a `404` — an interaction
 * with no linked `SubmissionPackage` yet (`submissionPkgRef === null`) is a legitimate, common
 * state, mirroring `getCreditInstrumentByFiling`'s own "empty, never an exception" contract
 * (`api/credit.ts`). Callers only invoke this when `submissionPkgRef` is already non-null, so the
 * 404 branch here is defense-in-depth, not the expected common path.
 */
export async function getSubmissionPackage(id: string, signal?: AbortSignal): Promise<SubmissionPackageView | null> {
  try {
    const { data } = await apiClient.get<SubmissionPackageView>(`/submission-packages/${id}`, { signal })
    return data
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null
    }
    throw err
  }
}
